import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  Mail, Plus, Trash2, Play, Clock, CheckCircle, AlertTriangle,
  Calendar, Users, Wrench, Package, TrendingUp, Ticket, Edit, Send, History, X,
  Eye, UserCheck, FileText, Copy
} from 'lucide-react';

const FREQ_OPTIONS = [
  { value: 'zilnic', label: 'Zilnic' },
  { value: 'saptamanal', label: 'Saptamanal' },
  { value: 'lunar', label: 'Lunar' },
  { value: 'custom', label: 'La interval (zile)' },
];

const DOW_OPTIONS = [
  { value: 0, label: 'Luni' }, { value: 1, label: 'Marti' }, { value: 2, label: 'Miercuri' },
  { value: 3, label: 'Joi' }, { value: 4, label: 'Vineri' }, { value: 5, label: 'Sambata' }, { value: 6, label: 'Duminica' },
];

const SECTION_ICONS = {
  pm_status: Wrench, tickets: Ticket, tech_kpis: Users, top_problematic: AlertTriangle,
  consumable_costs: Package, year_comparison: TrendingUp, pm_alerts: Clock, daily_tickets_detailed: FileText,
};

const ScheduledReportsPage = () => {
  const { hasRole } = useAuth();
  const [reports, setReports] = useState([]);
  const [sections, setSections] = useState([]);
  const [sectionChiefs, setSectionChiefs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [sending, setSending] = useState(null);
  const [, setNowTick] = useState(0);
  const [form, setForm] = useState({
    name: '', frequency: 'zilnic', custom_days: 7, day_of_week: 0, days_of_week: [], day_of_month: 1,
    hour: 8, minute: 0, recipients: [''], cc_recipients: [], sections: ['daily_tickets_detailed'],
    pm_alert_days: 14, ticket_period_days: 1, date_mode: 'yesterday', is_active: true
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [reportsRes, sectionsRes, chiefsRes, usersRes] = await Promise.all([
        api.get('/reports/scheduled'),
        api.get('/reports/available-sections'),
        api.get('/reports/section-chiefs'),
        api.get('/reports/all-users')
      ]);
      setReports(reportsRes.data);
      setSections(sectionsRes.data);
      setSectionChiefs(chiefsRes.data);
      setAllUsers(usersRes.data);
    } catch { toast.error('Eroare la incarcarea datelor'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Re-render every 30s so the "Urmatoarea rulare" label stays fresh
  useEffect(() => {
    const id = setInterval(() => setNowTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const resetForm = () => setForm({
    name: '', frequency: 'zilnic', custom_days: 7, day_of_week: 0, days_of_week: [], day_of_month: 1,
    hour: 8, minute: 0, recipients: [''], cc_recipients: [], sections: ['daily_tickets_detailed'],
    pm_alert_days: 14, ticket_period_days: 1, date_mode: 'yesterday', is_active: true
  });

  const openCreate = () => { resetForm(); setEditingId(null); setShowDialog(true); };
  const openEdit = (r) => {
    // Normalise days_of_week: use new array field, fallback to legacy single day
    const dows = r.days_of_week?.length ? r.days_of_week : (r.day_of_week != null ? [r.day_of_week] : []);
    setForm({
      name: r.name, frequency: r.frequency, custom_days: r.custom_days || 7,
      day_of_week: r.day_of_week ?? 0, days_of_week: dows, day_of_month: r.day_of_month ?? 1,
      hour: r.hour ?? 8, minute: r.minute ?? 0, recipients: r.recipients?.length > 0 ? r.recipients : [''],
      cc_recipients: r.cc_recipients || [],
      sections: r.sections || [], pm_alert_days: r.pm_alert_days || 14,
      ticket_period_days: r.ticket_period_days || 30,
      date_mode: r.date_mode || 'yesterday',
      is_active: r.is_active !== false
    });
    setEditingId(r.id);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Numele raportului este obligatoriu'); return; }
    const validRecipients = form.recipients.filter(r => r.trim());
    if (!validRecipients.length) { toast.error('Cel putin un destinatar este obligatoriu'); return; }
    if (!form.sections.length) { toast.error('Selectati cel putin o sectiune'); return; }
    const payload = { 
      ...form, 
      recipients: validRecipients,
      cc_recipients: form.cc_recipients.filter(r => r.trim())
    };
    try {
      if (editingId) {
        await api.put(`/reports/scheduled/${editingId}`, payload);
        toast.success('Raport actualizat!');
      } else {
        await api.post('/reports/scheduled', payload);
        toast.success('Raport programat creat!');
      }
      setShowDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la salvare'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti acest raport programat?')) return;
    try { await api.delete(`/reports/scheduled/${id}`); toast.success('Raport sters!'); fetchData(); }
    catch { toast.error('Eroare la stergere'); }
  };

  const handleSendNow = async (id) => {
    setSending(id);
    try {
      const res = await api.post(`/reports/scheduled/${id}/send`);
      if (res.data.success) toast.success('Raport trimis cu succes!');
      else toast.error(res.data.error || 'Eroare la trimitere');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la trimitere'); }
    finally { setSending(null); }
  };

  const handleSendTest = async () => {
    const validRecipients = form.recipients.filter(r => r.trim());
    if (!validRecipients.length) { toast.error('Adaugati cel putin un destinatar'); return; }
    setSending('test');
    try {
      const res = await api.post('/reports/send-test', { recipients: validRecipients });
      if (res.data.success) toast.success('Email test trimis!');
      else toast.error(res.data.error || 'Eroare la trimitere test');
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la trimitere test'); }
    finally { setSending(null); }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.post('/reports/preview', {
        name: form.name || 'Previzualizare Raport',
        sections: form.sections,
        pm_alert_days: form.pm_alert_days,
        ticket_period_days: form.ticket_period_days,
        frequency: form.frequency,
        date_mode: form.date_mode
      });
      if (res.data.success) {
        setPreviewHtml(res.data.html);
        setShowPreview(true);
      } else {
        toast.error(res.data.error || 'Eroare la generarea previzualizarii');
      }
    } catch (e) { toast.error('Eroare la generarea previzualizarii'); }
    finally { setPreviewLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/reports/history?limit=30');
      setHistory(res.data);
      setShowHistory(true);
    } catch { toast.error('Eroare la incarcarea istoricului'); }
  };

  const toggleSection = (key) => {
    setForm(prev => ({
      ...prev,
      sections: prev.sections.includes(key) ? prev.sections.filter(s => s !== key) : [...prev.sections, key]
    }));
  };

  const addRecipient = () => setForm(prev => ({ ...prev, recipients: [...prev.recipients, ''] }));
  const removeRecipient = (idx) => setForm(prev => ({ ...prev, recipients: prev.recipients.filter((_, i) => i !== idx) }));
  const updateRecipient = (idx, val) => setForm(prev => ({ ...prev, recipients: prev.recipients.map((r, i) => i === idx ? val : r) }));

  const addCcRecipient = () => setForm(prev => ({ ...prev, cc_recipients: [...prev.cc_recipients, ''] }));
  const removeCcRecipient = (idx) => setForm(prev => ({ ...prev, cc_recipients: prev.cc_recipients.filter((_, i) => i !== idx) }));
  const updateCcRecipient = (idx, val) => setForm(prev => ({ ...prev, cc_recipients: prev.cc_recipients.map((r, i) => i === idx ? val : r) }));

  const addAllSectionChiefs = () => {
    const chiefEmails = sectionChiefs.map(c => c.email).filter(e => e);
    const currentRecipients = form.recipients.filter(r => r.trim());
    const newRecipients = [...new Set([...currentRecipients, ...chiefEmails])];
    setForm(prev => ({ ...prev, recipients: newRecipients.length > 0 ? newRecipients : [''] }));
    toast.success(`${chiefEmails.length} sefi de sectie adaugati`);
  };

  const addSectionChief = (email) => {
    if (!email) return;
    const currentRecipients = form.recipients.filter(r => r.trim());
    if (!currentRecipients.includes(email)) {
      const newRecipients = [...currentRecipients, email];
      setForm(prev => ({ ...prev, recipients: newRecipients }));
      toast.success('Sef sectie adaugat');
    }
  };

  const addUserEmail = (email) => {
    if (!email) return;
    const currentRecipients = form.recipients.filter(r => r.trim());
    if (!currentRecipients.includes(email)) {
      const newRecipients = [...currentRecipients, email];
      setForm(prev => ({ ...prev, recipients: newRecipients }));
    }
  };

  const filteredUsers = allUsers.filter(u => {
    if (!userSearchTerm) return true;
    const term = userSearchTerm.toLowerCase();
    return (
      (u.full_name || '').toLowerCase().includes(term) ||
      (u.email || '').toLowerCase().includes(term) ||
      (u.role_label || '').toLowerCase().includes(term)
    );
  });

  const getFreqLabel = (r) => {
    const f = r.frequency;
    const hh = String(r.hour ?? 8).padStart(2, '0');
    const mm = String(r.minute ?? 0).padStart(2, '0');
    const t = `${hh}:${mm}`;
    if (f === 'zilnic') return `Zilnic la ${t}`;
    if (f === 'saptamanal') {
      const days = r.days_of_week && r.days_of_week.length
        ? r.days_of_week
        : (r.day_of_week != null ? [r.day_of_week] : [0]);
      const sorted = [...days].sort((a, b) => a - b);
      const names = sorted.map(d => DOW_OPTIONS[d]?.label?.slice(0, 3) || '').filter(Boolean);
      return `${names.join('/')} la ${t}`;
    }
    if (f === 'lunar') return `Zi ${r.day_of_month || 1} din luna la ${t}`;
    if (f === 'custom') return `La fiecare ${r.custom_days || 7} zile la ${t}`;
    return f;
  };

  /**
   * Compute the next scheduled run for a report in Romania local time.
   * Returns { date: Date, label: string } or null if inactive / cannot determine.
   */
  const computeNextRun = (r) => {
    if (!r || r.is_active === false) return null;
    const now = new Date();
    const hour = Number.isFinite(r.hour) ? r.hour : 8;
    const minute = Number.isFinite(r.minute) ? r.minute : 0;
    const lastSent = r.last_sent_at ? new Date(r.last_sent_at) : null;
    const lastSentDay = lastSent
      ? `${lastSent.getFullYear()}-${lastSent.getMonth()}-${lastSent.getDate()}`
      : null;

    const freq = r.frequency;
    const buildAt = (d) => {
      const dt = new Date(d);
      dt.setHours(hour, minute, 0, 0);
      return dt;
    };
    const jsDowToMonStart = (jsDow) => (jsDow + 6) % 7; // JS Sun=0 -> Mon=0 system

    if (freq === 'zilnic') {
      const today = buildAt(now);
      const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
      if (now <= today && (!lastSentDay || lastSentDay !== todayKey)) return { date: today };
      const tomorrow = buildAt(new Date(now.getTime() + 24 * 3600 * 1000));
      return { date: tomorrow };
    }

    if (freq === 'saptamanal') {
      const days = r.days_of_week && r.days_of_week.length
        ? r.days_of_week
        : (r.day_of_week != null ? [r.day_of_week] : [0]);
      for (let i = 0; i < 14; i++) {
        const d = new Date(now.getTime() + i * 24 * 3600 * 1000);
        const dow = jsDowToMonStart(d.getDay());
        if (!days.includes(dow)) continue;
        const runAt = buildAt(d);
        if (i === 0) {
          if (now > runAt) continue; // past target today
          const runKey = `${runAt.getFullYear()}-${runAt.getMonth()}-${runAt.getDate()}`;
          if (lastSentDay === runKey) continue; // already sent today
        }
        return { date: runAt };
      }
      return null;
    }

    if (freq === 'lunar') {
      const dom = r.day_of_month || 1;
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), dom);
      thisMonth.setHours(hour, minute, 0, 0);
      const thisKey = `${thisMonth.getFullYear()}-${thisMonth.getMonth()}-${thisMonth.getDate()}`;
      if (now <= thisMonth && (!lastSentDay || lastSentDay !== thisKey)) return { date: thisMonth };
      return { date: new Date(now.getFullYear(), now.getMonth() + 1, dom, hour, minute, 0, 0) };
    }

    if (freq === 'custom') {
      const days = r.custom_days || 7;
      if (!lastSent) return { date: buildAt(now) };
      const next = new Date(lastSent.getTime() + days * 24 * 3600 * 1000);
      next.setHours(hour, minute, 0, 0);
      return { date: next };
    }
    return null;
  };

  const formatNextRun = (d) => {
    if (!d) return '';
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const hmLbl = `${hh}:${mm}`;
    const today = new Date();
    const tomorrow = new Date(today.getTime() + 24 * 3600 * 1000);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    if (diffMs <= 0) return `In curand`;
    const diffMin = Math.round(diffMs / 60000);

    if (diffMin < 60) return `peste ${diffMin} min`;
    if (sameDay(d, today)) return `azi la ${hmLbl}`;
    if (sameDay(d, tomorrow)) return `maine la ${hmLbl}`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `peste ${diffH}h ${diffMin % 60}m`;

    const dateStr = d.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' });
    return `${dateStr} la ${hmLbl}`;
  };

  if (loading) return <div className="flex items-center justify-center py-20" data-testid="reports-loading"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div></div>;

  return (
    <div className="space-y-6" data-testid="scheduled-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rapoarte Programate</h1>
          <p className="text-slate-500 mt-1">Configurati rapoarte automate trimise prin email</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchHistory} data-testid="report-history-btn">
            <History className="w-4 h-4 mr-2" />Istoric trimiteri
          </Button>
          {hasRole('admin', 'manager') && (
            <Button className="bg-[#0052CC] hover:bg-[#003d99]" onClick={openCreate} data-testid="create-report-btn">
              <Plus className="w-4 h-4 mr-2" />RAPORT NOU
            </Button>
          )}
        </div>
      </div>

      {/* Reports List */}
      {reports.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Mail className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-500">Niciun raport programat</p>
            <p className="text-sm text-slate-400 mt-1">Creati primul raport automat pentru echipa</p>
            <Button className="mt-4 bg-[#0052CC]" onClick={openCreate}><Plus className="w-4 h-4 mr-2" />Creaza raport</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {reports.map(r => (
            <Card key={r.id} className={`transition-all ${r.is_active ? 'border-slate-200' : 'border-slate-100 opacity-60'}`} data-testid={`report-card-${r.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-[#0052CC]/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-[#0052CC]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{r.name}</h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={r.is_active ? 'default' : 'secondary'} className={r.is_active ? 'bg-emerald-100 text-emerald-700' : ''}>
                            {r.is_active ? 'Activ' : 'Inactiv'}
                          </Badge>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />{getFreqLabel(r)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(r.sections || []).map(s => {
                        const sec = sections.find(x => x.key === s);
                        const Icon = SECTION_ICONS[s] || Mail;
                        return (
                          <Badge key={s} variant="outline" className="text-[10px] gap-1">
                            <Icon className="w-3 h-3" />{sec?.label || s}
                          </Badge>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500 flex-wrap">
                      <span><Mail className="w-3 h-3 inline mr-1" />{(r.recipients || []).length} destinatari</span>
                      {(r.cc_recipients || []).length > 0 && <span><Copy className="w-3 h-3 inline mr-1" />{r.cc_recipients.length} CC</span>}
                      {r.last_sent_at && (
                        <span>Ultima trimitere: {new Date(r.last_sent_at).toLocaleDateString('ro-RO')} {r.last_sent_status === 'success' ? <CheckCircle className="w-3 h-3 inline text-green-500 ml-1" /> : <AlertTriangle className="w-3 h-3 inline text-red-500 ml-1" />}</span>
                      )}
                      {r.send_count > 0 && <span>Trimis de {r.send_count}x</span>}
                      {(() => {
                        const next = computeNextRun(r);
                        if (!next) return null;
                        return (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0052CC]/10 text-[#0052CC] font-semibold"
                            title={next.date.toLocaleString('ro-RO', { dateStyle: 'full', timeStyle: 'short' })}
                            data-testid={`next-run-${r.id}`}
                          >
                            <Calendar className="w-3 h-3" />Urmatoarea: {formatNextRun(next.date)}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => handleSendNow(r.id)} disabled={sending === r.id} data-testid={`send-report-${r.id}`}>
                      {sending === r.id ? <Clock className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`edit-report-${r.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(r.id)} data-testid={`delete-report-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={v => { setShowDialog(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editeaza Raport Programat' : 'Creaza Raport Programat Nou'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Name */}
            <div className="space-y-2">
              <Label>Nume raport *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="ex: Raport Zilnic Tichete" className="h-11" data-testid="report-name-input" />
            </div>

            {/* Frequency */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Frecventa trimitere *</Label>
                <Select value={form.frequency} onValueChange={v => setForm(p => ({ ...p, frequency: v }))}>
                  <SelectTrigger className="h-11" data-testid="report-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQ_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ora trimitere (format 24h)</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={String(form.hour ?? 8).padStart(2, '0')}
                    onValueChange={v => setForm(p => ({ ...p, hour: Number(v) }))}
                  >
                    <SelectTrigger className="h-11 flex-1" data-testid="report-hour-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')}>
                          {String(i).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-slate-500 font-bold">:</span>
                  <Select
                    value={String(form.minute ?? 0).padStart(2, '0')}
                    onValueChange={v => setForm(p => ({ ...p, minute: Number(v) }))}
                  >
                    <SelectTrigger className="h-11 flex-1" data-testid="report-minute-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {Array.from({ length: 60 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')}>
                          {String(i).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Frequency-specific options */}
            {form.frequency === 'saptamanal' && (
              <div className="space-y-2">
                <Label>Zilele saptamanii *</Label>
                <div className="flex flex-wrap gap-2">
                  {DOW_OPTIONS.map(d => {
                    const selected = (form.days_of_week || []).includes(d.value);
                    return (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => {
                          const curr = form.days_of_week || [];
                          const next = selected ? curr.filter(x => x !== d.value) : [...curr, d.value].sort();
                          setForm(p => ({ ...p, days_of_week: next }));
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                          selected
                            ? 'bg-[#0052CC] text-white border-[#0052CC]'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-[#0052CC] hover:text-[#0052CC]'
                        }`}
                        data-testid={`dow-btn-${d.value}`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
                {(form.days_of_week || []).length === 0 && (
                  <p className="text-xs text-red-500">Selectati cel putin o zi</p>
                )}
              </div>
            )}
            {form.frequency === 'lunar' && (
              <div className="space-y-2">
                <Label>Ziua din luna (1-28)</Label>
                <Input type="number" min={1} max={28} value={form.day_of_month} onChange={e => setForm(p => ({ ...p, day_of_month: Number(e.target.value) }))} className="h-11" />
              </div>
            )}
            {form.frequency === 'custom' && (
              <div className="space-y-2">
                <Label>La fiecare N zile</Label>
                <Input type="number" min={1} max={365} value={form.custom_days} onChange={e => setForm(p => ({ ...p, custom_days: Number(e.target.value) }))} className="h-11" />
              </div>
            )}

            {/* Date mode — only relevant when daily_tickets_detailed is selected */}
            {form.sections.includes('daily_tickets_detailed') && (
              <div className="space-y-2">
                <Label>Perioada tichete incluse</Label>
                <div className="flex gap-2">
                  {[
                    { value: 'yesterday', label: 'Ziua anterioară', desc: 'Tichete create/actualizate ieri' },
                    { value: 'today', label: 'Ziua curentă', desc: 'Tichete create/actualizate azi' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, date_mode: opt.value }))}
                      className={`flex-1 p-3 rounded-lg border text-left transition-all ${
                        form.date_mode === opt.value
                          ? 'border-[#0052CC] bg-blue-50 text-[#0052CC]'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`date-mode-${opt.value}`}
                    >
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Recipients */}
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span>Destinatari email *</span>
                <div className="flex gap-2">
                  {sectionChiefs.length > 0 && (
                    <Button variant="outline" size="sm" onClick={addAllSectionChiefs} className="text-xs h-7">
                      <UserCheck className="w-3 h-3 mr-1" />Sefi sectie ({sectionChiefs.length})
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setShowUserSelector(true)} className="text-xs h-7">
                    <Users className="w-3 h-3 mr-1" />Toti utilizatorii
                  </Button>
                  <Button variant="ghost" size="sm" onClick={addRecipient} className="text-xs h-7"><Plus className="w-3 h-3 mr-1" />Manual</Button>
                </div>
              </Label>
              
              {/* Selected recipients display */}
              {form.recipients.filter(r => r.trim()).length > 0 && (
                <div className="flex flex-wrap gap-1 p-2 bg-slate-50 rounded-lg border">
                  {form.recipients.filter(r => r.trim()).map((email, i) => {
                    const user = allUsers.find(u => u.email === email);
                    const isFiltered = user?.has_access_filter && form.sections.includes('daily_tickets_detailed');
                    return (
                      <Badge key={i} variant="secondary" className={`text-xs gap-1 pr-1 ${isFiltered ? 'border-amber-300 bg-amber-50' : ''}`}>
                        {user ? user.full_name : email}
                        {isFiltered && <span className="text-amber-600 text-[9px] ml-0.5">[F]</span>}
                        <button onClick={() => removeRecipient(form.recipients.indexOf(email))} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
              
              {/* Manual email input */}
              <div className="flex gap-2">
                <Input 
                  value={form.recipients[form.recipients.length - 1] || ''} 
                  onChange={e => updateRecipient(form.recipients.length - 1, e.target.value)} 
                  placeholder="sau introduceti email manual..." 
                  className="h-9 text-sm" 
                />
              </div>
            </div>

            {/* CC Recipients */}
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span>CC (copie)</span>
                <Button variant="ghost" size="sm" onClick={addCcRecipient} className="text-xs h-7"><Plus className="w-3 h-3 mr-1" />Adauga CC</Button>
              </Label>
              {form.cc_recipients.length === 0 ? (
                <p className="text-xs text-slate-400 py-2">Niciun destinatar CC adaugat</p>
              ) : (
                form.cc_recipients.map((r, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={r} onChange={e => updateCcRecipient(i, e.target.value)} placeholder="cc@exemplu.ro" className="h-10" data-testid={`report-cc-${i}`} />
                    <Button variant="ghost" size="sm" onClick={() => removeCcRecipient(i)} className="text-red-500 h-10 px-2"><X className="w-4 h-4" /></Button>
                  </div>
                ))
              )}
            </div>

            <Separator />

            {/* Sections */}
            <div className="space-y-3">
              <Label>Sectiuni incluse in raport *</Label>
              <div className="grid grid-cols-1 gap-2">
                {sections.map(s => {
                  const Icon = SECTION_ICONS[s.key] || Mail;
                  const checked = form.sections.includes(s.key);
                  const isHighlighted = s.key === 'daily_tickets_detailed';
                  return (
                    <div key={s.key}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${checked ? 'border-[#0052CC] bg-blue-50/50' : isHighlighted ? 'border-cyan-300 bg-cyan-50/30' : 'border-slate-200 hover:border-slate-300'}`}
                      onClick={() => toggleSection(s.key)} data-testid={`section-toggle-${s.key}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggleSection(s.key)} />
                      <Icon className={`w-5 h-5 flex-shrink-0 ${checked ? 'text-[#0052CC]' : isHighlighted ? 'text-cyan-600' : 'text-slate-400'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium ${checked ? 'text-slate-900' : 'text-slate-600'}`}>{s.label}</p>
                          {isHighlighted && <Badge className="text-[9px] bg-cyan-100 text-cyan-700">NOU</Badge>}
                        </div>
                        <p className="text-xs text-slate-400">{s.description}</p>
                        {isHighlighted && checked && (
                          <p className="text-[10px] text-amber-600 mt-1">
                            Fiecare destinatar TO primeste raport filtrat pe fabricile/locatiile sale. CC primeste raport complet.
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Separator />

            {/* Advanced Settings — show only when relevant sections are selected */}
            {(form.sections.some(s => ['pm_status', 'pm_alerts'].includes(s)) ||
              form.sections.some(s => ['tickets', 'tech_kpis', 'top_problematic', 'year_comparison', 'consumable_costs'].includes(s))) && (
              <div className="grid grid-cols-2 gap-4">
                {form.sections.some(s => ['pm_status', 'pm_alerts'].includes(s)) && (
                  <div className="space-y-2">
                    <Label>Alerta scadenta PM (zile inainte)</Label>
                    <Input type="number" min={1} max={90} value={form.pm_alert_days} onChange={e => setForm(p => ({ ...p, pm_alert_days: Number(e.target.value) }))} className="h-10" />
                  </div>
                )}
                {form.sections.some(s => ['tickets', 'tech_kpis', 'top_problematic', 'year_comparison', 'consumable_costs'].includes(s)) && (
                  <div className="space-y-2">
                    <Label>Perioada tichete (zile in urma)</Label>
                    <Input type="number" min={1} max={365} value={form.ticket_period_days} onChange={e => setForm(p => ({ ...p, ticket_period_days: Number(e.target.value) }))} className="h-10" />
                  </div>
                )}
              </div>
            )}

            {/* Active toggle */}
            <div className="flex items-center gap-3 p-3 rounded-lg border">
              <Checkbox checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} />
              <div>
                <p className="text-sm font-medium">Raport activ</p>
                <p className="text-xs text-slate-400">Dezactivati pentru a pastra configurarea fara a trimite</p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={handlePreview} disabled={previewLoading || form.sections.length === 0} data-testid="preview-report-btn">
              {previewLoading ? <Clock className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              Previzualizare
            </Button>
            <Button variant="outline" onClick={handleSendTest} disabled={sending === 'test'} data-testid="send-test-report-btn">
              {sending === 'test' ? <Clock className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Trimite test
            </Button>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Anuleaza</Button>
            <Button className="bg-[#0052CC]" onClick={handleSave} data-testid="save-report-btn">
              {editingId ? 'ACTUALIZEAZA' : 'CREAZA RAPORT'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Previzualizare Raport</DialogTitle>
            <DialogDescription>Acesta este modul in care va arata raportul trimis prin email</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto border rounded-lg bg-slate-100 p-4">
            <div 
              className="bg-white rounded shadow-sm" 
              dangerouslySetInnerHTML={{ __html: previewHtml }} 
            />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowPreview(false)}>Inchide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Istoric Trimiteri Rapoarte</DialogTitle></DialogHeader>
          {history.length === 0 ? (
            <p className="text-center py-8 text-slate-400">Nicio trimitere inregistrata</p>
          ) : (
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.id} className={`flex items-center gap-3 p-3 rounded-lg border ${h.success ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}>
                  {h.success ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" /> : <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{h.report_name || 'Raport'}</p>
                    <p className="text-xs text-slate-500">{(h.recipients || []).join(', ')}</p>
                    {(h.cc_recipients || []).length > 0 && (
                      <p className="text-xs text-slate-400">CC: {h.cc_recipients.join(', ')}</p>
                    )}
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <p>{h.sent_at ? new Date(h.sent_at).toLocaleDateString('ro-RO') : '-'}</p>
                    <p>{h.sent_at ? new Date(h.sent_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User Selector Dialog */}
      <Dialog open={showUserSelector} onOpenChange={setShowUserSelector}>
        <DialogContent className="max-w-xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Selecteaza Destinatari</DialogTitle>
            <DialogDescription>Click pe utilizatori pentru a-i adauga ca destinatari</DialogDescription>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative">
            <Input 
              value={userSearchTerm} 
              onChange={e => setUserSearchTerm(e.target.value)}
              placeholder="Cauta dupa nume, email sau rol..."
              className="h-10 pr-8"
            />
            {userSearchTerm && (
              <button onClick={() => setUserSearchTerm('')} className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Quick select buttons */}
          <div className="flex flex-wrap gap-2 py-2 border-b">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => {
              const adminEmails = allUsers.filter(u => u.role === 'admin').map(u => u.email);
              adminEmails.forEach(e => addUserEmail(e));
              toast.success(`${adminEmails.length} administratori adaugati`);
            }}>
              + Toti Administratorii
            </Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => {
              const managerEmails = allUsers.filter(u => u.role === 'manager').map(u => u.email);
              managerEmails.forEach(e => addUserEmail(e));
              toast.success(`${managerEmails.length} manageri adaugati`);
            }}>
              + Toti Managerii
            </Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={addAllSectionChiefs}>
              + Toti Sefii Sectie
            </Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => {
              const techEmails = allUsers.filter(u => u.role === 'technician').map(u => u.email);
              techEmails.forEach(e => addUserEmail(e));
              toast.success(`${techEmails.length} tehnicieni adaugati`);
            }}>
              + Toti Tehnicienii
            </Button>
          </div>
          
          {/* User list */}
          <div className="flex-1 overflow-y-auto max-h-[400px] space-y-1">
            {filteredUsers.length === 0 ? (
              <p className="text-center py-8 text-slate-400">Niciun utilizator gasit</p>
            ) : (
              filteredUsers.map(u => {
                const isSelected = form.recipients.includes(u.email);
                return (
                  <div 
                    key={u.id}
                    onClick={() => {
                      if (isSelected) {
                        setForm(prev => ({ ...prev, recipients: prev.recipients.filter(r => r !== u.email) }));
                      } else {
                        addUserEmail(u.email);
                      }
                    }}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'border-[#0052CC] bg-blue-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
                  >
                    <Checkbox checked={isSelected} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{u.full_name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                      {u.plant_names && u.plant_names.length > 0 && (
                        <p className="text-[10px] text-slate-400 mt-0.5">{u.plant_names.join(', ')}</p>
                      )}
                    </div>
                    <div className="text-right flex flex-col items-end gap-1">
                      <Badge variant="outline" className="text-[10px]">{u.role_label}</Badge>
                      {u.has_access_filter ? (
                        <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-300">Filtrat</Badge>
                      ) : (
                        <Badge className="text-[9px] bg-slate-100 text-slate-500">Complet</Badge>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          <DialogFooter className="mt-4 border-t pt-4">
            <div className="flex-1 text-sm text-slate-500">
              <p>{form.recipients.filter(r => r.trim()).length} destinatari selectati</p>
              {form.sections.includes('daily_tickets_detailed') && (
                <p className="text-[10px] text-amber-600 mt-1">
                  Utilizatorii cu acces limitat (Filtrat) vor primi raportul filtrat pe fabricile/locatiile lor
                </p>
              )}
            </div>
            <Button variant="outline" onClick={() => setShowUserSelector(false)}>Gata</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledReportsPage;
